import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { PATCHNOTES, formatDate, type Patchnotes } from '@werkdelta/shared';
import { t } from '../i18n';

/**
 * Patch-Notes nach einem Update (D-147). Die neueste Version erscheint einmal je Benutzer
 * und Browser; der Gesehen-Stand liegt bewusst im Browser (localStorage), nicht auf dem
 * Server: Das Demo-Konto teilen sich viele Besucher, und ein Serverfeld bräuchte Migration,
 * Rechte und Auditpfad. Ohne Speicher (privater Modus) erscheint die Mitteilung erneut —
 * lieber einmal zu oft als nie.
 */
const SPEICHER = 'werkdelta.patchnotes.gesehen';

function gesehen(userId: string): string | null {
  try {
    return window.localStorage.getItem(`${SPEICHER}.${userId}`);
  } catch {
    return null;
  }
}

function merke(userId: string, version: string): void {
  try {
    window.localStorage.setItem(`${SPEICHER}.${userId}`, version);
  } catch {
    // ohne Speicher erscheint die Mitteilung beim nächsten Öffnen erneut
  }
}

function Eintrag({ p, ebene }: { p: Patchnotes; ebene: 'einzeln' | 'archiv' }) {
  const Abschnitt = ebene === 'einzeln' ? 'h3' : 'h4';
  return (
    <section>
      {ebene === 'archiv' && <h3>{t('patchnotes.version', { version: p.version })}</h3>}
      <p className="patchnotes-meta">
        {t('patchnotes.veroeffentlicht', { datum: formatDate(p.datum) })}
        {p.vorher && <> · {t('patchnotes.vorher', { version: p.vorher })}</>}
      </p>
      <p>{t(p.kurz)}</p>
      {p.abschnitte.map((a) => (
        <div key={a.art}>
          <Abschnitt>{t(`patchnotes.art.${a.art}`)}</Abschnitt>
          <ul>
            {a.punkte.map((k) => (
              <li key={k}>{t(k)}</li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

function PatchnotesDialog({
  eintraege,
  onSchliessen,
}: {
  eintraege: readonly Patchnotes[];
  onSchliessen: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titelRef = useRef<HTMLHeadingElement>(null);
  const titelId = useId();
  // Auslöser beim ersten Rendern erfassen — vor showModal und vor dem Titelfokus.
  const [vorher] = useState(() =>
    document.activeElement instanceof HTMLElement ? document.activeElement : null,
  );
  // Als echtes modales Overlay öffnen (wie useConfirm). Kein close() beim Abbau: Das Entfernen
  // aus dem Dokument beendet den modalen Zustand, und ein programmatisches close() erzeugte ein
  // verspätetes `close`-Ereignis (StrictMode hängt Effekte im Entwicklungsmodus doppelt ein).
  // Der Fokus geht auf den Titel, damit der lange Inhalt von oben gelesen wird
  // (WAI-ARIA-Dialogmuster), und beim Schliessen zurück an den Auslöser (etwa die Fusszeile).
  useEffect(() => {
    const el = ref.current;
    if (el && !el.open) el.showModal();
    titelRef.current?.focus();
    return () => {
      if (vorher && vorher !== document.body && vorher.isConnected) vorher.focus();
    };
  }, [vorher]);
  const einzeln = eintraege.length === 1 ? eintraege[0] : undefined;
  return (
    <dialog
      ref={ref}
      className="wd-dialog patchnotes"
      aria-modal="true"
      aria-labelledby={titelId}
      onCancel={(e) => {
        e.preventDefault();
        onSchliessen();
      }}
      // Ohne vorherige Nutzerinteraktion schliesst der Browser bei Escape direkt, ohne
      // `cancel`. Auch das gilt als gesehen — ausser der Dialog ist schon wieder offen.
      onClose={() => {
        if (!ref.current?.open) onSchliessen();
      }}
    >
      <h2 id={titelId} ref={titelRef} tabIndex={-1}>
        {einzeln
          ? t('patchnotes.dialogTitel', { version: einzeln.version })
          : t('patchnotes.titel')}
      </h2>
      <div className="patchnotes-inhalt">
        {eintraege.map((p) => (
          <Eintrag key={p.version} p={p} ebene={einzeln ? 'einzeln' : 'archiv'} />
        ))}
      </div>
      <div className="aktionen">
        <button type="button" className="knopf-primaer" onClick={onSchliessen}>
          {t('patchnotes.verstanden')}
        </button>
      </div>
    </dialog>
  );
}

/**
 * Zeigt nach der Anmeldung die neueste Version, wenn sie für diesen Benutzer in diesem
 * Browser noch nicht bestätigt ist; `oeffneAlle` öffnet alle Patch-Notes (Fusszeile).
 * Jedes Schliessen — Knopf oder Escape — gilt als gesehen.
 */
export function usePatchnotes(userId: string): { oeffneAlle: () => void; dialog: ReactNode } {
  const neueste = PATCHNOTES[0];
  const [modus, setModus] = useState<'neueste' | 'alle' | null>(() =>
    neueste && gesehen(userId) !== neueste.version ? 'neueste' : null,
  );
  const schliessen = () => {
    if (neueste) merke(userId, neueste.version);
    setModus(null);
  };
  const eintraege = modus === 'neueste' && neueste ? [neueste] : PATCHNOTES;
  return {
    oeffneAlle: () => setModus('alle'),
    dialog: modus ? <PatchnotesDialog eintraege={eintraege} onSchliessen={schliessen} /> : null,
  };
}
